import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../api-base-url.token';

export type QualityStatsPeriod = 'all' | '7d' | '30d';

export interface Feedback {
  feedback_id: string;
  created_at: string;
  queue_id: string;
  queue_name: string;
  comment: string | null;
  score: number | null;
  reported_bucket: number | null;
  wants_reply: boolean;
  contact_email: string | null;
  contact_tel: string | null;
  replied_at: string | null;
  replied_by_name: string | null;
  has_corrective_action: boolean;
  reply_content: string | null;
  contact_method: 'EMAIL' | 'PHONE' | 'CLOSED_WITHOUT_CONTACT' | null;
  corrective_action_status: 'pending' | 'in_progress' | 'completed' | null;
  corrective_action_created_at: string | null;
  corrective_action_assigned_to: string | null;
  form_responses: Array<{ question_text: string; rating: number | null }> | null;
  establishment_id?: string;
  establishment_name?: string;
}

export interface FeedbackResponse {
  feedbacks: Feedback[];
  total_elements: number;
  total_pages: number;
  current_page: number;
  size: number;
}

export interface FeedbackResponse {
  feedbacks: Feedback[];
  counts: {
    total_actions: number;
    archived_feedbacks: number;
  };
}

export interface BulkReplyResult {
  feedback_id: string;
  status: 'sent' | 'failed';
  error: string | null;
}

export interface BulkReplyResponse {
  total: number;
  success: number;
  failed: number;
  results: BulkReplyResult[];
}

export interface EstablishmentStats {
  avg_score: number | null;
  total_feedbacks: number;
  total_qr: number;
  participation_rate: number;
  score_5_pct: number;
  score_4_pct: number;
  score_3_pct: number;
  score_2_pct: number;
  score_1_pct: number;
  first_feedback_date: string | null;
  google_review_clicks: number;
}

// DEPRECATED (2026-09-08) : alerte qualité retirée du front (page Satisfaction + bouton top bar
// shell.component.ts) -- ce type et getQualityAlert() ci-dessous ne sont plus appelés. Voir
// satisfaction.component.ts pour le détail complet.
export interface QualityAlert {
  action_required: boolean;
  critical_feedbacks_count: number;
  has_active_action: boolean;
  message: string;
}

export interface MonthlySatisfactionPoint {
  month: string;
  avg_score: number | null;
  total_feedbacks: number;
}

@Injectable({
  providedIn: 'root'
})
export class SatisfactionService {
  private http = inject(HttpClient);
  private base = inject(API_BASE_URL);

  getEstablishmentStats(id: string, period: QualityStatsPeriod = 'all'): Observable<EstablishmentStats> {
    const params = new HttpParams().set('period', period);
    return this.http.get<EstablishmentStats>(
      `${this.base}/api/v1/satisfaction/establishments/${id}/stats`,
      { params }
    );
  }

  getFeedbacks(establishmentId: string): Observable<FeedbackResponse> {
    return this.http.get<FeedbackResponse>(`${this.base}/api/v1/satisfaction/establishments/${establishmentId}/feedbacks`);
  }

  /** Feedbacks de tous les établissements de l'organisation en un seul appel, plutôt qu'un appel par établissement. */
  getOrgFeedbacks(): Observable<Record<string, FeedbackResponse>> {
    return this.http.get<Record<string, FeedbackResponse>>(`${this.base}/api/v1/satisfaction/org/feedbacks`);
  }

  sendReplyEmail(feedbackId: string, subject: string, message: string): Observable<any> {
    return this.http.post(`${this.base}/api/v1/satisfaction/feedbacks/${feedbackId}/reply`, {
      subject,
      message
    });
  }

  sendBulkReplyEmail(feedbackIds: string[], subject: string, message: string): Observable<BulkReplyResponse> {
    return this.http.post<BulkReplyResponse>(`${this.base}/api/v1/satisfaction/feedbacks/bulk-reply`, {
      feedback_ids: feedbackIds,
      subject,
      message
    });
  }

  getMonthlyHistory(establishmentId: string): Observable<MonthlySatisfactionPoint[]> {
    return this.http.get<MonthlySatisfactionPoint[]>(
      `${this.base}/api/v1/satisfaction/establishments/${establishmentId}/monthly-history`
    );
  }

  getQualityAlert(establishmentId: string): Observable<QualityAlert> {
    return this.http.get<QualityAlert>(`${this.base}/api/v1/satisfaction/establishments/${establishmentId}/quality-alert`);
  }

  markAsCalled(feedbackId: string, replyContent?: string): Observable<any> {
    const body = replyContent ? { reply_content: replyContent } : {};
    return this.http.post(`${this.base}/api/v1/satisfaction/feedbacks/${feedbackId}/mark-called`, body);
  }

  markAsTreated(feedbackId: string): Observable<any> {
    return this.http.post(`${this.base}/api/v1/satisfaction/feedbacks/${feedbackId}/mark-treated`, {});
  }

  archiveFeedback(feedbackId: string): Observable<any> {
    return this.http.put(`${this.base}/api/v1/satisfaction/feedbacks/${feedbackId}/archive`, {});
  }

  exportArchivedFeedbacks(establishmentId: string): Observable<Blob> {
    return this.http.get(`${this.base}/api/v1/satisfaction/establishments/${establishmentId}/feedbacks/archived/export`, {
      responseType: 'blob'
    });
  }

  updateCallContent(feedbackId: string, content: string): Observable<any> {
    return this.http.post(`${this.base}/api/v1/satisfaction/feedbacks/${feedbackId}/mark-called`, {
      reply_content: content
    });
  }
}